import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Button, InputAdornment, MenuItem, Paper, Stack, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import React, { useMemo, useState } from 'react';
import { computeHoldings } from '../../domain/holdings';
import { downloadCsv } from '../../lib/csv';
import { compactMoney, date, DEMO_TODAY } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { AboutThisPage, PageHeader, StatCard, StatGrid } from '../../ui/primitives';
import HoldingsTable from '../common/HoldingsTable';

export default function RegisterPage() {
  const scope = useTenantScope();
  const { state } = useRegistry();
  const toast = useToast();
  const [fundId, setFundId] = useState('ALL');
  const [classId, setClassId] = useState('ALL');
  const [asAt, setAsAt] = useState(DEMO_TODAY);
  const [search, setSearch] = useState('');

  const holdings = useMemo(() => {
    const all =
      asAt === DEMO_TODAY
        ? scope.holdings
        : computeHoldings(scope.ledger, scope.classes, state.commitments, asAt);
    const term = search.trim().toLowerCase();
    return all.filter((holding) => {
      if (fundId !== 'ALL' && holding.fundId !== fundId) return false;
      if (classId !== 'ALL' && holding.classId !== classId) return false;
      if (!term) return true;
      const holder = scope.holderById.get(holding.holderId);
      return Boolean(
        holder &&
        (holder.name.toLowerCase().includes(term) ||
          holder.accountNumber.toLowerCase().includes(term)),
      );
    });
  }, [scope, state.commitments, fundId, classId, asAt, search]);

  const classOptions = scope.classes.filter(
    (unitClass) => fundId === 'ALL' || unitClass.fundId === fundId,
  );

  return (
    <>
      <PageHeader
        title="Register"
        subtitle="The register of unitholders, rebuilt from the ledger for any date."
        actions={
          <>
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              variant="outlined"
              onClick={() => {
                downloadCsv(`register-${asAt}.csv`, [
                  ['Account', 'Holder', 'Type', 'Fund', 'Class', 'Units', 'Value', 'Holder since'],
                  ...holdings.map((holding) => {
                    const holder = scope.holderById.get(holding.holderId);
                    return [
                      holder?.accountNumber ?? '',
                      holder?.name ?? '',
                      holder?.type ?? '',
                      scope.fundById.get(holding.fundId)?.code ?? '',
                      scope.classById.get(holding.classId)?.name ?? '',
                      holding.units,
                      holding.value.toFixed(2),
                      holding.firstDate,
                    ];
                  }),
                ]);
                toast(`Register as at ${date(asAt)} downloaded`);
              }}
            >
              Export CSV
            </Button>
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              variant="outlined"
              onClick={() =>
                toast(
                  `Signed register extract (PDF) as at ${date(asAt)} generated for the auditor`,
                  'info',
                )
              }
            >
              Register extract (PDF)
            </Button>
          </>
        }
      />
      <AboutThisPage>
        <p>
          The trust deed and Corporations Act require a current register of members. Because
          holdings are derived from an append-only ledger, the register can be reproduced exactly as
          it stood on any past date — useful for record dates, audits and disputes. Change the
          &quot;As at&quot; date to see this.
        </p>
      </AboutThisPage>
      <StatGrid>
        <StatCard label="Holdings" value={holdings.length} />
        <StatCard
          label="Unique holders"
          value={new Set(holdings.map((holding) => holding.holderId)).size}
        />
        <StatCard
          label="Value"
          value={compactMoney(sumBy(holdings, (holding) => holding.value))}
          hint={`As at ${date(asAt)}`}
        />
        <StatCard
          label="Units on issue"
          value={sumBy(holdings, (holding) => holding.units).toLocaleString('en-AU', {
            maximumFractionDigits: 0,
          })}
        />
      </StatGrid>
      <Paper variant="outlined">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ p: 2 }}>
          <TextField
            size="small"
            placeholder="Search holder or account number"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            label="Fund"
            value={fundId}
            onChange={(event) => {
              setFundId(event.target.value);
              setClassId('ALL');
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="ALL">All funds</MenuItem>
            {scope.funds.map((fund) => (
              <MenuItem key={fund.id} value={fund.id}>
                {fund.code} · {fund.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Class"
            value={classId}
            onChange={(event) => setClassId(event.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="ALL">All classes</MenuItem>
            {classOptions.map((unitClass) => (
              <MenuItem key={unitClass.id} value={unitClass.id}>
                {scope.fundById.get(unitClass.fundId)?.code} · {unitClass.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="As at"
            value={asAt}
            onChange={(event) => setAsAt(event.target.value || DEMO_TODAY)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: DEMO_TODAY }}
          />
        </Stack>
        <HoldingsTable holdings={holdings} />
      </Paper>
    </>
  );
}
